class CartDrawer extends HTMLElement {
  constructor() {
    super();

    // Add comprehensive error handling
    try {
      this.addEventListener(
        "keyup",
        (evt) => evt.code === "Escape" && this.close()
      );

      const overlay = this.querySelector("#CartDrawer-Overlay");
      if (overlay) {
        overlay.addEventListener("click", this.close.bind(this));
      }

      this.setHeaderCartIconAccessibility();
      this.setupMobileSafariFixes();
    } catch (error) {
      console.warn("Error in CartDrawer constructor:", error);
    }
  }

  setupMobileSafariFixes() {
    try {
      // Fix for mobile Safari transform issues
      this.style.webkitTransform = "translateZ(0)";
      this.style.transform = "translateZ(0)";
      this.style.webkitBackfaceVisibility = "hidden";
      this.style.backfaceVisibility = "hidden";

      // Fix for mobile Safari scroll issues
      const drawerInner = this.querySelector(".drawer__inner");
      if (drawerInner) {
        drawerInner.style.webkitOverflowScrolling = "touch";
      }
    } catch (error) {
      console.warn("Error in setupMobileSafariFixes:", error);
    }
  }

  setHeaderCartIconAccessibility() {
    try {
      const cartLink = document.querySelector("#cart-icon-bubble");
      if (!cartLink) {
        console.warn("Cart icon bubble not found");
        return;
      }

      cartLink.setAttribute("role", "button");
      cartLink.setAttribute("aria-haspopup", "dialog");

      // Remove existing listeners to prevent duplicates
      const newCartLink = cartLink.cloneNode(true);
      cartLink.parentNode.replaceChild(newCartLink, cartLink);

      newCartLink.addEventListener("click", (event) => {
        event.preventDefault();
        this.open(newCartLink);
      });

      newCartLink.addEventListener("keydown", (event) => {
        if (event.code.toUpperCase() === "SPACE") {
          event.preventDefault();
          this.open(newCartLink);
        }
      });
    } catch (error) {
      console.warn("Error in setHeaderCartIconAccessibility:", error);
    }
  }

  open(triggeredBy) {
    try {
      if (triggeredBy) this.setActiveElement(triggeredBy);

      const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
      if (cartDrawerNote && !cartDrawerNote.hasAttribute("role")) {
        this.setSummaryAccessibility(cartDrawerNote);
      }

      // Mobile Safari compatibility - ensure proper timing
      requestAnimationFrame(() => {
        this.classList.add("animate", "active");

        // Force mobile Safari to show the drawer properly
        if (/iPad|iPhone|iPod/.test(navigator.userAgent)) {
          const drawerInner = this.querySelector(".drawer__inner");
          if (drawerInner) {
            // Force the transform to be applied
            drawerInner.style.webkitTransform = "translateX(0)";
            drawerInner.style.transform = "translateX(0)";
            drawerInner.style.visibility = "visible";
          }

          // Ensure the drawer itself is visible
          this.style.visibility = "visible";
          this.style.opacity = "1";
        }
      });

      // Add transition end listener with error handling
      const transitionEndHandler = () => {
        try {
          const containerToTrapFocusOn = this.classList.contains("is-empty")
            ? this.querySelector(".drawer__inner-empty")
            : document.getElementById("CartDrawer");
          const focusElement =
            this.querySelector(".drawer__inner") ||
            this.querySelector(".drawer__close");
          if (containerToTrapFocusOn && focusElement) {
            trapFocus(containerToTrapFocusOn, focusElement);
          }
        } catch (error) {
          console.warn("Error in transition end handler:", error);
        }
      };

      this.addEventListener("transitionend", transitionEndHandler, {
        once: true,
      });

      document.body.classList.add("overflow-hidden");
    } catch (error) {
      console.error("Error in cart drawer open:", error);
      // Fallback: force show the drawer
      this.classList.add("active");
      document.body.classList.add("overflow-hidden");
    }
  }

  close() {
    try {
      this.classList.remove("active");
      if (this.activeElement) {
        removeTrapFocus(this.activeElement);
      }
      document.body.classList.remove("overflow-hidden");
    } catch (error) {
      console.error("Error in cart drawer close:", error);
      // Fallback: force hide the drawer
      this.classList.remove("active");
      document.body.classList.remove("overflow-hidden");
    }
  }

  setSummaryAccessibility(cartDrawerNote) {
    try {
      if (!cartDrawerNote) return;

      cartDrawerNote.setAttribute("role", "button");
      cartDrawerNote.setAttribute("aria-expanded", "false");

      if (
        cartDrawerNote.nextElementSibling &&
        cartDrawerNote.nextElementSibling.getAttribute("id")
      ) {
        cartDrawerNote.setAttribute(
          "aria-controls",
          cartDrawerNote.nextElementSibling.id
        );
      }

      cartDrawerNote.addEventListener("click", (event) => {
        try {
          const details = event.currentTarget.closest("details");
          if (details) {
            event.currentTarget.setAttribute(
              "aria-expanded",
              !details.hasAttribute("open")
            );
          }
        } catch (error) {
          console.warn("Error in cart drawer note click handler:", error);
        }
      });

      if (cartDrawerNote.parentElement) {
        cartDrawerNote.parentElement.addEventListener("keyup", onKeyUpEscape);
      }
    } catch (error) {
      console.warn("Error in setSummaryAccessibility:", error);
    }
  }

  renderContents(parsedState) {
    try {
      const drawerInner = this.querySelector(".drawer__inner");
      if (drawerInner && drawerInner.classList.contains("is-empty")) {
        drawerInner.classList.remove("is-empty");
      }

      this.productId = parsedState.id;
      this.getSectionsToRender().forEach((section) => {
        const sectionElement = section.selector
          ? document.querySelector(section.selector)
          : document.getElementById(section.id);

        if (!sectionElement) {
          console.warn(
            `Section element not found: ${section.id || section.selector}`
          );
          return;
        }

        const sectionHTML = this.getSectionInnerHTML(
          parsedState.sections[section.id],
          section.selector
        );

        if (sectionHTML) {
          sectionElement.innerHTML = sectionHTML;
        }
      });

      // Add delay to ensure DOM is updated
      setTimeout(() => {
        try {
          const overlay = this.querySelector("#CartDrawer-Overlay");
          if (overlay) {
            // Remove existing listeners to prevent duplicates
            const newOverlay = overlay.cloneNode(true);
            overlay.parentNode.replaceChild(newOverlay, overlay);
            newOverlay.addEventListener("click", this.close.bind(this));
          }
          this.open();
        } catch (error) {
          console.warn("Error in renderContents timeout:", error);
          this.open();
        }
      }, 100);
    } catch (error) {
      console.error("Error in renderContents:", error);
      // Fallback: try to open the drawer anyway
      this.open();
    }
  }

  getSectionInnerHTML(html, selector = ".shopify-section") {
    try {
      if (!html) return "";

      const parser = new DOMParser();
      const parsed = parser.parseFromString(html, "text/html");
      const element = parsed.querySelector(selector);

      return element ? element.innerHTML : "";
    } catch (error) {
      console.warn("Error in getSectionInnerHTML:", error);
      return "";
    }
  }

  getSectionsToRender() {
    return [
      {
        id: "cart-drawer",
        selector: "#CartDrawer",
      },
      {
        id: "cart-icon-bubble",
      },
    ];
  }

  getSectionDOM(html, selector = ".shopify-section") {
    try {
      if (!html) return null;

      const parser = new DOMParser();
      const parsed = parser.parseFromString(html, "text/html");
      return parsed.querySelector(selector);
    } catch (error) {
      console.warn("Error in getSectionDOM:", error);
      return null;
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  // Safari-specific method to handle cart updates
  handleSafariCartUpdate(cartData) {
    try {
      const isSafari =
        /iPad|iPhone|iPod/.test(navigator.userAgent) &&
        /Safari/.test(navigator.userAgent);

      if (isSafari) {
        // Force a small delay for Safari to process the update
        setTimeout(() => {
          this.renderContents(cartData);
        }, 100);
      } else {
        this.renderContents(cartData);
      }
    } catch (error) {
      console.error("Error in handleSafariCartUpdate:", error);
      // Fallback
      this.renderContents(cartData);
    }
  }
}

// Add global error handler for addEventListener errors
if (typeof window !== "undefined") {
  const originalAddEventListener = EventTarget.prototype.addEventListener;
  EventTarget.prototype.addEventListener = function (type, listener, options) {
    try {
      if (this === null || this === undefined) {
        console.warn(
          "Attempted to add event listener to null/undefined element:",
          type
        );
        return;
      }
      return originalAddEventListener.call(this, type, listener, options);
    } catch (error) {
      console.warn("Error adding event listener:", error);
    }
  };
}

customElements.define("cart-drawer", CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: "CartDrawer",
        section: "cart-drawer",
        selector: ".drawer__inner",
      },
      {
        id: "cart-icon-bubble",
        section: "cart-icon-bubble",
        selector: ".shopify-section",
      },
    ];
  }
}

customElements.define("cart-drawer-items", CartDrawerItems);
